/**
 * Supabase Auth Service for ConnectX
 *
 * Replaces the legacy Express/FastAPI auth endpoints with
 * direct Supabase Auth SDK calls.
 */
import { supabase } from '../lib/supabaseClient';
import type { Profile } from '../types/database.types';
import type { User, Session, AuthChangeEvent } from '@supabase/supabase-js';

const SESSION_LOCK_STOLEN_MESSAGE = 'was released because another request stole it';

function isSessionLockContentionError(error: unknown): boolean {
  return (
    error instanceof Error &&
    error.message.toLowerCase().includes(SESSION_LOCK_STOLEN_MESSAGE)
  );
}

function delay(ms: number) {
  return new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });
}

// ─── Sign Up ──────────────────────────────────────────────
export async function signUp(email: string, password: string, fullName: string, role: string = 'job_seeker') {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: { full_name: fullName, role },
    },
  });

  if (error) throw error;

  // The handle_new_user trigger creates the profile row automatically.
  // Update the role if not default.
  if (data.user && role !== 'job_seeker') {
    await supabase
      .from('profiles')
      .update({ role } as any)
      .eq('id', data.user.id);
  }

  return data;
}

// ─── Sign In (email + password) ──────────────────────────
export async function signIn(email: string, password: string) {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;

  // Best-effort: do not fail sign-in if profile row or RLS blocks last_login update
  if (data.user) {
    const { error: profileError } = await supabase
      .from('profiles')
      .update({ last_login: new Date().toISOString() } as any)
      .eq('id', data.user.id);
    if (profileError) {
      console.warn('signIn: could not update profiles.last_login', profileError);
    }
  }

  return data;
}

// ─── Sign Out ─────────────────────────────────────────────
export async function signOut() {
  localStorage.removeItem('user');
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}

// ─── Google OAuth ─────────────────────────────────────────

/** Stored before redirect so the callback page knows login vs sign-up flow. */
export const GOOGLE_OAUTH_INTENT_STORAGE_KEY = 'reffconnect_google_oauth_intent';

export type GoogleOAuthIntent = 'login' | 'signup';

/** Read intent without removing (safe under React StrictMode double-mount). */
export function peekGoogleOAuthIntent(): GoogleOAuthIntent {
  try {
    return sessionStorage.getItem(GOOGLE_OAUTH_INTENT_STORAGE_KEY) === 'login'
      ? 'login'
      : 'signup';
  } catch {
    return 'signup';
  }
}

/** Call once the OAuth callback has finished routing the user (success or failure). */
export function clearGoogleOAuthIntentStorage(): void {
  try {
    sessionStorage.removeItem(GOOGLE_OAUTH_INTENT_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export async function signInWithGoogle(options?: { intent?: GoogleOAuthIntent }) {
  const intent: GoogleOAuthIntent = options?.intent ?? 'signup';
  try {
    sessionStorage.setItem(GOOGLE_OAUTH_INTENT_STORAGE_KEY, intent);
  } catch {
    /* ignore (e.g. private mode) */
  }
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: `${window.location.origin}/oauth/google/success`,
    },
  });
  if (error) throw error;
  return data;
}

// ─── Password Reset ──────────────────────────────────────
export async function resetPasswordRequest(email: string) {
  const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/forgot-password?step=reset`,
  });
  if (error) throw error;
  return data;
}

export async function updatePassword(newPassword: string) {
  const { data, error } = await supabase.auth.updateUser({ password: newPassword });
  if (error) throw error;
  return data;
}

// ─── Session helpers ─────────────────────────────────────
export async function getSession() {
  const maxAttempts = 3;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      if (error) throw error;
      return session;
    } catch (error) {
      if (isSessionLockContentionError(error)) {
        if (attempt < maxAttempts) {
          await delay(attempt * 50);
          continue;
        }
        return null;
      }
      throw error;
    }
  }

  return null;
}

export async function getUser() {
  const { data: { user }, error } = await supabase.auth.getUser();
  if (error) throw error;
  return user;
}

// ─── Profile helpers ─────────────────────────────────────
export async function getProfile(userId: string): Promise<Profile | null> {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .single();
  if (error) {
    console.error('Error fetching profile:', error);
    return null;
  }
  return data;
}

/**
 * Fallback profile lookup by email.  Works when Google OAuth creates a
 * separate user ID from the original email/password account.
 *
 * Requires an RLS policy on `profiles` that allows:
 *   SELECT … WHERE auth.email() = primary_email
 */
export async function getProfileByEmail(email: string): Promise<Profile | null> {
  if (!email) return null;
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('primary_email', email.toLowerCase().trim())
    .maybeSingle();
  if (error) {
    console.warn('getProfileByEmail:', error.message);
    return null;
  }
  return data;
}

/**
 * `user_onboarding.user_id` often FK-references `profiles.id`. New OAuth users
 * can be signed in before a `profiles` row exists if the DB trigger is missing
 * or failed — then finishing onboarding hits a foreign key violation.
 */
export async function ensureProfileForAuthUser(user: User): Promise<void> {
  const existing = await getProfile(user.id);
  if (existing) return;

  const primaryEmail = (user.email ?? '').trim();
  if (!primaryEmail) {
    throw new Error(
      'Your account has no email on file, so we cannot create your profile. Contact support or try signing up with email.'
    );
  }

  const meta = user.user_metadata || {};
  const fullName =
    (typeof meta.full_name === 'string' && meta.full_name.trim()) ||
    (typeof meta.name === 'string' && meta.name.trim()) ||
    primaryEmail.split('@')[0] ||
    'User';

  const isGoogle = user.identities?.some((i) => i.provider === 'google') ?? false;

  const { error } = await supabase.from('profiles').upsert(
    {
      id: user.id,
      full_name: fullName,
      primary_email: primaryEmail,
      auth_provider: isGoogle ? 'google' : 'local',
      role: 'job_seeker',
      is_active: true,
      is_verified: false,
      email_verified: Boolean(user.email_confirmed_at),
      credits: 0,
    } as any,
    { onConflict: 'id', ignoreDuplicates: true }
  );

  if (error) {
    if (error.code === '23505') return;
    throw error;
  }

  const after = await getProfile(user.id);
  if (!after) {
    throw new Error(
      'Could not create your profile (database policy may block inserts). Ask an admin to verify the profiles RLS policy and the new-user trigger.'
    );
  }
}

/** True when this user has finished app onboarding (saved in DB). Used to block Google-from-login for brand-new OAuth users, who still get a profiles row from triggers. */
export async function hasCompletedOnboarding(userId: string): Promise<boolean> {
  const { data, error } = await supabase
    .from('user_onboarding')
    .select('completed_at')
    .eq('user_id', userId)
    .maybeSingle();
  if (error) {
    console.error('hasCompletedOnboarding:', error);
    return false;
  }
  return Boolean(data?.completed_at);
}

export async function updateProfile(userId: string, updates: Partial<Profile>) {
  const { data, error } = await supabase
    .from('profiles')
    .update(updates as any)
    .eq('id', userId)
    .select()
    .single();
  if (error) throw error;
  return data;
}

// ─── Auth state listener ─────────────────────────────────
export function onAuthStateChange(
  callback: (event: AuthChangeEvent, session: Session | null) => void
) {
  return supabase.auth.onAuthStateChange(callback);
}

// ─── Resend verification email ───────────────────────────
export async function resendVerificationEmail(email: string) {
  const { data, error } = await supabase.auth.resend({
    type: 'signup',
    email,
  });
  if (error) throw error;
  return data;
}

// Re-export types for convenience
export type { User, Session };
